import { SideMark } from "./SideMark.tsx";
import { SIDE_COLOR } from "../lib/progress.ts";
import { formatSet, SIDE_NAME, SIDES, type Side, type SideFigures, type SidesDraft } from "../lib/session.ts";

/**
 * The side a one-sided set is being typed for. One side at a time on the
 * steppers, at their full size: two sides next to each other halved every
 * button, on a phone, for hands that are shaking.
 *
 * Nothing is picked at first. The side a set starts on changes from one set
 * to the next, and a side assumed is a side logged wrong without a word.
 */
export function SidePicker({
  draft,
  dropSet,
  title,
  onPick,
}: {
  draft: SidesDraft;
  dropSet: boolean;
  /** What is being typed, asked with the side while none is picked. */
  title: string;
  onPick: (side: Side) => void;
}) {
  return (
    <div>
      {draft.active === null && <p className="label px-0.5 pb-1">{title} · par quel côté ?</p>}
      <div className="grid grid-cols-2 gap-2" role="group" aria-label="Côté">
        {SIDES.map((side) => {
          const active = draft.active === side;
          const typed = draft.typed[side];
          return (
            <button
              key={side}
              type="button"
              aria-pressed={active}
              onClick={() => onPick(side)}
              className={[
                "flex h-14 min-w-0 items-center gap-2.5 rounded-card border px-3.5 text-left active:opacity-70",
                active ? "" : "border-line bg-surface",
              ].join(" ")}
              style={
                active
                  ? {
                      borderColor: SIDE_COLOR[side],
                      background: `color-mix(in oklab, ${SIDE_COLOR[side]} 18%, transparent)`,
                    }
                  : undefined
              }
            >
              <SideMark side={side} size={12} />
              <span className="min-w-0">
                <span className="wide block text-[0.95rem] leading-tight font-bold capitalize">{SIDE_NAME[side]}</span>
                <span className="num block truncate text-[0.72rem] text-ink-mute">
                  {active ? "en cours" : typed ? summary(typed, dropSet) : "à faire"}
                </span>
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/** "15,8 × 9", "15,8 × 9 → 11,5 × 6": what was left on the steppers for a side. */
function summary(figures: SideFigures, dropSet: boolean): string {
  return formatSet({
    weightKg: figures.weightKg,
    reps: figures.reps,
    dropWeightKg: dropSet ? figures.dropWeightKg : null,
    dropReps: dropSet ? figures.dropReps : null,
  });
}
